import { usePrivy } from '@privy-io/react-auth';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { useTransfers } from '@/features/account/api/get-transfers';
import { usePrivyAccount } from '@/hooks';
import { useListenKey, useRefreshListenKey } from '@/lib/api/listen-key';
import { logConsole } from '@/lib/utils';
import { useAccountStore } from '@/store/use-account-store';
import useFavoritesStore from '@/store/use-favorites-store';
import { EventType } from '@/types';
import { ErrorCode } from '@/types/enums';
import { sendEvent } from '@/utils/analytics';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';

const MAX_RETRIES = 5;

export default function ApiHandler() {
  const { address, hasAuth } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      hasAuth: state.hasAuth,
    })),
  );

  const authParams = useAccountStore(
    useShallow((state) => state.getAuthParams()),
  );

  const { email } = usePrivyAccount();
  const { user } = usePrivy();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => sendEvent(EventType.APP_LAUNCHED, { email }), []);

  const { boot, shutdown } = useIntercom();
  const { i18n } = useTranslation();

  useEffect(() => {
    // https://www.intercom.com/help/en/articles/9423842-enforce-the-language-of-the-messenger-fin-ai-agent-and-workflows-for-specific-users#h_1055838be4
    const languageOverride = (() => {
      switch (i18n.language) {
        case 'en':
          return 'en';
        case 'zh':
          return 'zh-CN';
      }
    })();
    boot({
      actionColor: 'rgb(var(--primary))',
      languageOverride,
      alignment: 'left',
      email,
      name: email ?? address ?? user?.id,
      ...(user ? { createdAt: user.createdAt.getTime(), userId: user.id } : {}),
      customAttributes: {
        address,
      },
    });
    return () => {
      shutdown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, email, user?.id, user?.createdAt, i18n.language]);

  const keyExpiry = authParams?.listenKeyExpiry ?? 0; // in case of null, subtract and comparision works weird.

  const {
    mutateAsync: getListenKey,
    error: _listenKeyError,
    reset: resetRefreshKey,
  } = useListenKey(); // if this error object is not null and is unauthorized, set to false
  const listenKeyError = _listenKeyError as unknown as ErrorCode;

  const {
    mutateAsync: refreshListenKey,
    error: _refreshListenKeyError,
    isPending: isListenKeyPending,
  } = useRefreshListenKey();

  useEffect(() => {
    if (!hasAuth || isListenKeyPending || !address) return;
    if (!authParams || authParams.accountGroup === 2) return;

    if (
      listenKeyError === ErrorCode.UNAUTHORIZED ||
      listenKeyError === ErrorCode.ACCOUNT_NOT_FOUND
    ) {
      resetRefreshKey();
      return;
    }

    const currentTimestamp = useAccountStore.getState().getAccurateTime();
    if (
      !!authParams?.listenKey &&
      authParams.listenKeyExpiry > currentTimestamp
    )
      return;

    let retryCount = 1;

    const interval = window.setInterval(() => {
      if (retryCount > MAX_RETRIES) {
        window.clearInterval(interval);
        toast.error('Connection failed. Please refresh the page.');
      } else {
        getListenKey(address);
        retryCount++;
      }
    }, 2000); // Retry after 2 seconds

    return () => {
      window.clearInterval(interval);
    }; // Cleanup timeout on unmount or dependency change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address,
    hasAuth,
    authParams?.apiKey,
    isListenKeyPending,
    listenKeyError,
    authParams?.listenKey,
    authParams?.listenKeyExpiry,
    authParams?.accountGroup,
  ]);

  useEffect(() => {
    const currentTimestamp = useAccountStore.getState().getAccurateTime();
    let refreshTimeoutId: NodeJS.Timeout;

    if (address && authParams?.listenKey && keyExpiry > currentTimestamp) {
      refreshTimeoutId = setTimeout(() => {
        logConsole(false, 'Refresh Listen Key');
        refreshListenKey(address);
      }, keyExpiry - currentTimestamp);
    }

    return () => {
      clearTimeout(refreshTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, authParams?.listenKey, keyExpiry]);

  const { data: transferData } = useTransfers();

  useEffect(() => {
    if (!transferData) return;
    transferData.forEach((res) => {
      const transfer = transferResponseToTransfer(res);
      handleTransferForStore(transfer, { skipAccountUpdate: true });
    });
  }, [transferData]);

  useEffect(() => {
    useFavoritesStore.getState().initFavorites();
  }, [address]);

  return <></>;
}
